import { Button, IconFont, Toast } from '@byecode/ui'
import type { DataSourceAbstract } from '@lighthouse/core'
import { Modal } from '@mantine/core'
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'

import { extractMdFiles, type MdFileEntry } from '../../utils/markdown'
import { SettingStep } from './SettingStep'
import { SummaryDialog } from './SummaryDialog'
import type { MarkdownImportParams, MarkdownImportSummary } from './types'
import { UploadStep } from './UploadStep'

export * from './types'

export interface MarkdownImporterProps {
    appId: string
    envId: string
    dataSource: DataSourceAbstract
    open: boolean
    onClose: () => void
    onImport: (params: MarkdownImportParams) => Promise<MarkdownImportSummary>
}

const ImportContainer = styled.div`
    outline: none;
    padding: 24px 20px;
    user-select: none;
`

const NavigatorContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
`

const NavigatorTitle = styled.p`
    font-size: 20px;
    font-weight: 500;
`

const NavigatorCloseIcon = styled(IconFont)`
    cursor: pointer;
`

const FooterContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
    gap: 8px;
`

const SCxLoading = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 40px 0;
`

const SCxLoadingTitle = styled.div`
    font-weight: 600;
    font-size: 16px;
    color: var(--color-black);
`

const SCxLoadingDescription = styled.div`
    font-size: 14px;
    color: var(--color-gray-400);
`

const matchableFieldTypes: Set<string> = new Set(['text', 'number', 'url', 'email', 'phoneNumber', 'singleSelect'])

const Spinner = styled(IconFont)`
    animation: spin 1s linear infinite;
    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }
`

export const MarkdownImporter: React.FC<MarkdownImporterProps> = ({ dataSource, open, onClose, onImport }) => {
    const [step, setStep] = useState(1)
    const [entries, setEntries] = useState<MdFileEntry[]>([])
    const [noteFieldId, setNoteFieldId] = useState('')
    const [matchFieldId, setMatchFieldId] = useState('')
    const [loading, setLoading] = useState(false)
    const [summary, setSummary] = useState<MarkdownImportSummary | null>(null)

    const reset = useCallback(() => {
        setStep(1)
        setEntries([])
        setNoteFieldId('')
        setMatchFieldId('')
        setSummary(null)
        setLoading(false)
    }, [])

    const handleClose = useCallback(() => {
        if (loading) {
            return
        }
        reset()
        onClose()
    }, [loading, onClose, reset])

    const handleAddFile = useCallback(async (file: File) => {
        try {
            const extracted = await extractMdFiles(file)
            if (extracted.length === 0) {
                Toast.error('未在文件中找到 .md 文件')
                return
            }
            setEntries(prev => {
                const existing = new Set(prev.map(item => item.fileName))
                return [...prev, ...extracted.filter(item => !existing.has(item.fileName))]
            })
        } catch (error) {
            Toast.error(error instanceof Error ? error.message : '文件解析失败')
        }
    }, [])

    const handleRemove = useCallback((index: number) => {
        setEntries(prev => prev.filter((_, i) => i !== index))
    }, [])

    // 进入设置步时自动选中首个笔记字段与映射字段
    useEffect(() => {
        if (step !== 2) {
            return
        }
        const fields = Object.values(dataSource.schema ?? {})
        const firstNote = fields.find(field => field.type === 'notes')
        const firstMatch = fields.find(field => matchableFieldTypes.has(field.type))
        setNoteFieldId(prev => prev || firstNote?.id || '')
        setMatchFieldId(prev => prev || firstMatch?.id || '')
    }, [dataSource.schema, step])

    const canNext = entries.length > 0
    const canImport = Boolean(noteFieldId && matchFieldId)

    const handleImport = useCallback(async () => {
        if (!canImport) {
            return
        }
        setLoading(true)
        try {
            const result = await onImport({ files: entries, noteFieldId, matchFieldId })
            setSummary(result)
        } catch (error) {
            Toast.error(error instanceof Error ? error.message : '导入失败')
        } finally {
            setLoading(false)
        }
    }, [canImport, entries, matchFieldId, noteFieldId, onImport])

    const handleSummaryClose = useCallback(() => {
        reset()
        onClose()
    }, [onClose, reset])

    return (
        <>
            <Modal
                centered
                closeOnClickOutside={false}
                padding={0}
                radius={12}
                size={690}
                overlayColor="var(--color-black)"
                overlayOpacity={0.75}
                overlayBlur={3}
                withCloseButton={false}
                opened={open && !summary}
                onClose={handleClose}
            >
                <ImportContainer>
                    <NavigatorContainer>
                        <NavigatorTitle>导入 Markdown</NavigatorTitle>
                        <NavigatorCloseIcon type="Close" size={16} onClick={handleClose} />
                    </NavigatorContainer>
                    {step === 1 && <UploadStep entries={entries} onAddFile={handleAddFile} onRemove={handleRemove} />}
                    {step === 2 && (
                        <SettingStep
                            dataSource={dataSource}
                            noteFieldId={noteFieldId}
                            matchFieldId={matchFieldId}
                            onNoteFieldChange={setNoteFieldId}
                            onMatchFieldChange={setMatchFieldId}
                        />
                    )}
                    <FooterContainer>
                        {step === 2 && (
                            <Button
                                type="text"
                                icon={<IconFont type="ArrowLeftSmall" fill="var(--color-gray-400)" />}
                                onClick={() => setStep(1)}
                            >
                                上一步
                            </Button>
                        )}
                        <Button onClick={handleClose}>取消</Button>
                        {step === 1 && (
                            <Button type="primary" disabled={!canNext} onClick={() => setStep(2)}>
                                下一步
                            </Button>
                        )}
                        {step === 2 && (
                            <Button type="primary" disabled={!canImport} onClick={handleImport}>
                                开始导入
                            </Button>
                        )}
                    </FooterContainer>
                </ImportContainer>
            </Modal>

            {loading && (
                <Modal centered opened size={344} radius={12} withCloseButton={false} onClose={() => {}}>
                    <SCxLoading>
                        <Spinner type="CircleNotch" size={36} />
                        <SCxLoadingTitle>正在导入 Markdown...</SCxLoadingTitle>
                        <SCxLoadingDescription>请不要刷新页面，导入开始后不可中途停止</SCxLoadingDescription>
                    </SCxLoading>
                </Modal>
            )}

            <Modal centered opened={Boolean(summary)} size={480} radius={12} withCloseButton={false} onClose={handleSummaryClose}>
                {summary && <SummaryDialog summary={summary} onClose={handleSummaryClose} />}
            </Modal>
        </>
    )
}
